import { useState, useEffect } from 'react';
import { UseApi } from './UseApi';

// Main hook to check if user needs onboarding
export const UseOnboardingStatus = (user, isAuthenticated) => {
  const api = UseApi();
  const [needsOnboarding, setNeedsOnboarding] = useState(null); // null = checking, true = needs, false = complete
  const [isChecking, setIsChecking] = useState(false);
  const [shopProfile, setShopProfile] = useState(null);

  useEffect(() => {
    if (isAuthenticated && user && needsOnboarding === null) {
      checkOnboardingStatus();
    }
  }, [isAuthenticated, user, needsOnboarding]);

  const checkOnboardingStatus = async () => {
    try {
      setIsChecking(true);
      // FIRST: Check if user is a studio member - they don't need onboarding
      try {
        const subscriptionData = await api.fetchWithAuth('/subscription/details/');
        
        // If user is a studio member (not owner), skip onboarding
        if (subscriptionData?.is_studio_member && subscriptionData?.studio_membership?.role !== 'owner') {
          setNeedsOnboarding(false);
          setIsChecking(false);
          return;
        }
      } catch (subError) {
        // Continue with normal onboarding check
      }
      
      // Check if user has completed their business profile
      const profile = await api.fetchWithAuth('/shop/profile/');
      setShopProfile(profile);
      
      // Required fields for onboarding completion
      const hasShopName = profile?.name && profile.name.trim() !== '';
      const hasAddress = profile?.address && profile.address.trim() !== '';
      const hasCity = profile?.city && profile.city.trim() !== '';
      const hasState = profile?.state && profile.state.trim() !== '';
      const hasZipCode = profile?.zip_code && profile.zip_code.trim() !== '';
      
      // User needs onboarding if any required field is missing
      const isOnboardingComplete = hasShopName && hasAddress && hasCity && hasState && hasZipCode;
      
      console.log('📊 Onboarding Status Check:', {
        userEmail: user?.email,
        hasShopName,
        hasAddress,
        hasCity,
        hasState,
        hasZipCode,
        isOnboardingComplete,
        profileExists: !!profile
      });
      
      setNeedsOnboarding(!isOnboardingComplete);
      
    } catch (error) {
      console.error('❌ Error checking onboarding status:', error);
      
      // If shop profile doesn't exist or API fails, assume they need onboarding
      // This handles new users who don't have a shop profile yet
      setNeedsOnboarding(true);
      setShopProfile(null);
    } finally {
      setIsChecking(false);
    }
  };

  return {
    needsOnboarding,
    isChecking,
    shopProfile,
    recheckOnboarding: checkOnboardingStatus
  };
};

// Simple function version for use in components that don't need reactive updates
export const NeedsOnboarding = async (user, api) => {
  try {
    if (!user) {
      return false;
    }
    // FIRST: Check if user is a studio member - they don't need onboarding
    try {
      const subscriptionData = await api.fetchWithAuth('/subscription/details/');
      
      if (subscriptionData?.is_studio_member && subscriptionData?.studio_membership?.role !== 'owner') {
        return false;
      }
    } catch (subError) {
    }
    
    // Check if user has completed their business profile
    const shopProfile = await api.fetchWithAuth('/shop/profile/');
    
    // Required fields for onboarding completion
    const hasShopName = shopProfile?.name && shopProfile.name.trim() !== '';
    const hasAddress = shopProfile?.address && shopProfile.address.trim() !== '';
    const hasCity = shopProfile?.city && shopProfile.city.trim() !== '';
    const hasState = shopProfile?.state && shopProfile.state.trim() !== '';
    const hasZipCode = shopProfile?.zip_code && shopProfile.zip_code.trim() !== '';
    
    // User needs onboarding if any required field is missing
    const isOnboardingComplete = hasShopName && hasAddress && hasCity && hasState && hasZipCode;
    
    console.log('📊 NeedsOnboarding result:', {
      userEmail: user.email,
      isOnboardingComplete,
      needsOnboarding: !isOnboardingComplete
    });
    
    return !isOnboardingComplete;
    
  } catch (error) {
    console.error('❌ Error in NeedsOnboarding check:', error);
    // If API fails, assume they need onboarding (safe default for new users)
    return true;
  }
};

// Helper function to get onboarding completion percentage
export const GetOnboardingProgress = (shopProfile) => {
  if (!shopProfile) {
    return 0;
  }
  
  const requiredFields = [
    shopProfile?.name?.trim(),
    shopProfile?.address?.trim(), 
    shopProfile?.city?.trim(),
    shopProfile?.state?.trim(),
    shopProfile?.zip_code?.trim()
  ];
  
  const completedFields = requiredFields.filter(field => field && field !== '').length;
  const totalFields = requiredFields.length;
  const progress = Math.round((completedFields / totalFields) * 100);
  
  console.log('📊 Onboarding progress:', {
    completedFields,
    totalFields,
    progress: `${progress}%`
  });
  
  return progress;
};

// Helper function to get missing required fields
export const GetMissingRequiredFields = (shopProfile) => {
  const missing = [];
  
  if (!shopProfile?.name?.trim()) missing.push('Shop Name');
  if (!shopProfile?.address?.trim()) missing.push('Street Address');
  if (!shopProfile?.city?.trim()) missing.push('City');
  if (!shopProfile?.state?.trim()) missing.push('State');
  if (!shopProfile?.zip_code?.trim()) missing.push('ZIP Code');
  return missing;
};

// Helper to check if user should skip onboarding (for existing complete users)
export const ShouldSkipOnboarding = (user, shopProfile) => {
  // Skip onboarding if user has a complete profile
  const hasAllRequiredFields = shopProfile?.name && 
                              shopProfile?.address && 
                              shopProfile?.city && 
                              shopProfile?.state && 
                              shopProfile?.zip_code;
  
  if (hasAllRequiredFields) {
    return true;
  }
  return false;
};

// Helper to validate required onboarding fields
export const ValidateOnboardingData = (formData) => {
  const errors = {};
  
  // Shop name validation
  if (!formData.shopName || formData.shopName.trim() === '') {
    errors.shopName = 'Shop name is required';
  } else if (formData.shopName.trim().length < 2) {
    errors.shopName = 'Shop name must be at least 2 characters';
  } else if (formData.shopName.trim().length > 100) {
    errors.shopName = 'Shop name must be less than 100 characters';
  }
  
  // Address validation
  if (!formData.address || formData.address.trim() === '') {
    errors.address = 'Street address is required';
  } else if (formData.address.trim().length < 5) {
    errors.address = 'Please enter a complete street address';
  }
  
  // City validation
  if (!formData.city || formData.city.trim() === '') {
    errors.city = 'City is required';
  } else if (formData.city.trim().length < 2) {
    errors.city = 'Please enter a valid city name';
  }
  
  // State validation
  if (!formData.state || formData.state.trim() === '') {
    errors.state = 'State is required';
  }
  
  // ZIP code validation
  if (!formData.zipCode || formData.zipCode.trim() === '') {
    errors.zipCode = 'ZIP code is required';
  } else if (!/^\d{5}(-\d{4})?$/.test(formData.zipCode.trim())) {
    errors.zipCode = 'Please enter a valid ZIP code (12345 or 12345-6789)';
  }
  
  // Phone validation (optional field)
  if (formData.phone && formData.phone.trim() !== '') {
    // Basic phone validation - should be 10+ digits
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (phoneDigits.length < 10) {
      errors.phone = 'Please enter a valid phone number';
    }
  }
  
  const isValid = Object.keys(errors).length === 0;
  
  console.log('📋 Onboarding data validation:', {
    isValid,
    errors: Object.keys(errors),
    formData: {
      hasShopName: !!formData.shopName,
      hasAddress: !!formData.address,
      hasCity: !!formData.city,
      hasState: !!formData.state,
      hasZipCode: !!formData.zipCode,
      hasPhone: !!formData.phone
    }
  });
  
  return {
    isValid,
    errors
  };
};

// Export default object with all functions (alternative import style)
export default {
  UseOnboardingStatus,
  NeedsOnboarding,
  GetOnboardingProgress,
  GetMissingRequiredFields,
  ShouldSkipOnboarding,
  ValidateOnboardingData
};